import {
  configurationsGetRfpConfigurationOptions,
  useHeyApi,
} from "@platform/api";
import { useQuery } from "@tanstack/react-query";

import type { ConfigurationResponse } from "@platform/api";

export interface RfpConfiguration {
  /**
   * The configuration the RFP is spread against: its own selection, or the
   * published default when it has none. Null only when nothing resolved at all
   * (`source: "none"`) — no selection and no published default.
   */
  configuration: ConfigurationResponse | null;
  /**
   * Where the configuration came from: `rfp` for an analyst's own selection,
   * `default` for the published fallback, `none` when nothing resolved.
   */
  source: RfpConfigurationSource;
  isLoading: boolean;
  isError: boolean;
}

type RfpConfigurationSource = "rfp" | "default" | "none";

/**
 * The MP configuration an RFP is spread against (CD-386). It drives which
 * benefit fields the side-by-side pages show and which template the export
 * renders with, so the picker and the export drawer read the same query.
 */
export const useRfpConfiguration = (
  ecoRfpId: string | undefined,
): RfpConfiguration => {
  const { centroPortalClient } = useHeyApi();

  const hasRfp = !!ecoRfpId;

  const { data, isLoading, isError } = useQuery({
    ...configurationsGetRfpConfigurationOptions({
      path: { rfpId: ecoRfpId as string },
      client: centroPortalClient,
    }),
    enabled: hasRfp,
  });

  return {
    configuration: data?.configuration ?? null,
    source: data?.source ?? "none",
    isLoading: hasRfp && isLoading,
    isError,
  };
};

export default useRfpConfiguration;
